/* @jsx h */
import { defineComponent, h, ref, computed, watch, PropType } from "vue";
import { RestaurantCustomer } from "@lemmonhq/types";
import { api } from "@app/data/api";

export interface CustomerProfileBadgeProps {
  customer?: RestaurantCustomer | null;
  customerId?: string;
  email?: string;
}

export const CustomerProfileBadge = defineComponent<CustomerProfileBadgeProps>({
  name: "CustomerProfileBadge",
  props: {
    customer: {
      type: Object as PropType<RestaurantCustomer | null>,
      default: null,
    },
    customerId: {
      type: String,
      default: undefined,
    },
    email: {
      type: String,
      default: undefined,
    },
  },
  setup(props) {
    const loading = ref(false);
    const error = ref("");
    const customerData = ref<RestaurantCustomer | null>(null);

    // Watch for customer prop changes
    watch(
      () => props.customer,
      (newCustomer) => {
        if (newCustomer) {
          customerData.value = newCustomer;
        }
      },
      { immediate: true },
    );

    // Fetch customer data if needed
    watch(
      () => props.customerId,
      async (newCustomerId) => {
        if (!newCustomerId || customerData.value) return;

        try {
          loading.value = true;
          error.value = "";
          const data = await api.getCustomer(newCustomerId);
          customerData.value = data;
        } catch (e) {
          console.error("Failed to fetch customer:", e);
          error.value = "Failed to load customer data";
        } finally {
          loading.value = false;
        }
      },
      { immediate: true },
    );

    const formatCurrency = (value: number | undefined) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(Number(value || 0));
    };

    const getStatusColor = (level?: string | null) => {
      const l = level?.toLowerCase();
      if (l === "vip")
        return "text-purple-400 bg-purple-500/10 border-purple-500/20";
      if (l === "regular")
        return "text-blue-400 bg-blue-500/10 border-blue-500/20";
      if (l === "new")
        return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
      return "text-base-content/60 bg-base-200 border-base-300";
    };

    const getStatusLabel = (level?: string | null) => {
      if (!level) return "Guest";
      return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
    };

    const getReliabilityScore = (customer: RestaurantCustomer) => {
      const total = customer.total_reservations || 0;
      if (total === 0) return null;
      const completed = customer.total_reservations_completed || 0;
      return Math.round((completed / total) * 100);
    };

    const getReliabilityColor = (score: number | null) => {
      if (score === null) return "text-base-content/50";
      if (score >= 90) return "text-emerald-400";
      if (score >= 70) return "text-blue-400";
      if (score >= 50) return "text-amber-400";
      return "text-rose-400";
    };

    return () => {
      // Loading state — skeleton matching compact row layout
      if (loading.value) {
        return (
          <div class="flex items-center gap-4 py-1.5">
            <div class="flex items-center gap-1.5">
              <div class="w-4 h-4 rounded bg-base-300/50 animate-pulse"></div>
              <div class="w-16 h-3 rounded bg-base-300/50 animate-pulse"></div>
              <div class="w-6 h-4 rounded bg-base-300/60 animate-pulse"></div>
            </div>
            <div class="w-px h-4 bg-base-content/10" />
            <div class="flex items-center gap-1.5">
              <div class="w-4 h-4 rounded bg-base-300/50 animate-pulse"></div>
              <div class="w-14 h-3 rounded bg-base-300/50 animate-pulse"></div>
              <div class="w-4 h-4 rounded bg-base-300/60 animate-pulse"></div>
            </div>
          </div>
        );
      }

      // Error state
      if (error.value && !customerData.value) {
        return (
          <div class="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl">
            <div class="flex items-center gap-2 text-sm text-rose-400">
              <span class="material-symbols-outlined text-lg">error</span>
              <span>{error.value}</span>
            </div>
          </div>
        );
      }

      // No customer data - show guest
      if (!customerData.value) {
        const displayName = props.email || "Guest";
        return (
          <div class="p-4 bg-base-200 rounded-xl border border-base-300">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-base-300 flex items-center justify-center">
                <span class="material-symbols-outlined text-base-content/50">
                  person
                </span>
              </div>
              <div>
                <div class="font-medium text-accent">{displayName}</div>
                <div class="text-xs text-base-content/50">New Guest</div>
              </div>
            </div>
          </div>
        );
      }

      const customer = customerData.value;
      const displayName = customer.name || props.email || "Guest";
      const statusColor = getStatusColor(customer.status_level);
      const statusLabel = getStatusLabel(customer.status_level);
      const hasNoshows = (customer.total_reservations_noshows || 0) > 0;
      const hasCancellations =
        (customer.total_reservations_cancellations || 0) > 0;
      const noshowCount = customer.total_reservations_noshows || 0;
      const avgOrderValue =
        customer.total_orders && customer.total_orders > 0
          ? Number(customer.total_spent || 0) / customer.total_orders
          : 0;
      const statCardClass =
        "min-h-[92px] min-w-0 bg-base-100/70 rounded-lg p-1 border border-base-300/50 flex flex-col items-center justify-between text-center";
      const statLabelClass =
        "max-w-full text-[11px] font-medium uppercase leading-tight text-center break-words";
      const statHelperClass =
        "text-xs text-base-content/50 leading-tight text-center mt-0.5";

      return (
        <div class="">
          {/* Header */}

          {/* Stats Row — compact */}
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Reservations */}
            <div class={statCardClass}>
              <div class="min-w-0 flex flex-col items-center">
                <div class="min-w-0 flex flex-col items-center gap-1 text-base-content/50 mb-1">
                  <span class="material-symbols-outlined text-lg">
                    event_seat
                  </span>
                  <span class={statLabelClass}>Reservations</span>
                </div>
                <div class="text-xl font-bold text-accent">
                  {customer.total_reservations || 0}
                </div>
              </div>
            </div>

            {/* No Shows */}
            <div class={statCardClass}>
              <div class="min-w-0 flex flex-col items-center">
                <div
                  class={`min-w-0 flex flex-col items-center gap-1 mb-1 ${noshowCount > 0 ? "text-error" : "text-base-content/50"}`}
                >
                  <span class="material-symbols-outlined text-lg">
                    person_cancel
                  </span>
                  <span class={statLabelClass}>No Shows</span>
                </div>
                <div
                  class={`text-xl font-bold ${noshowCount > 0 ? "text-error" : "text-accent"}`}
                >
                  {noshowCount}
                </div>
              </div>
            </div>

            {/* Orders */}
            {customer.total_orders && customer.total_orders > 0 ? (
              <div class={statCardClass}>
                <div class="min-w-0 flex flex-col items-center">
                  <div class="min-w-0 flex flex-col items-center gap-1 text-emerald-400 mb-1">
                    <span class="material-symbols-outlined text-lg">
                      shopping_bag
                    </span>
                    <span class={statLabelClass}>Orders</span>
                  </div>
                  <div class="text-xl font-bold text-accent">
                    {customer.total_orders}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Average Order Value */}
            {avgOrderValue > 0 ? (
              <div class={statCardClass}>
                <div class="min-w-0 flex flex-col items-center">
                  <div class="min-w-0 flex flex-col items-center gap-1 text-amber-400 mb-1">
                    <span class="material-symbols-outlined text-lg">
                      trending_up
                    </span>
                    <span class={statLabelClass}>Avg Order</span>
                  </div>
                  <div class="text-xl font-bold text-accent">
                    {formatCurrency(avgOrderValue)}
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Tags */}
          {customer.restaurant_tags && customer.restaurant_tags.length > 0 ? (
            <div class="px-4 pb-4">
              <div class="text-xs font-medium text-base-content/50 uppercase tracking-wide mb-2">
                Tags
              </div>
              <div class="flex flex-wrap gap-1.5">
                {customer.restaurant_tags.map((tag) => (
                  <span
                    key={tag}
                    class="px-2 py-1 bg-base-100 border border-base-300 rounded-md text-xs text-base-content/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      );
    };
  },
});
